import { useLaunchDarkly } from '@/platform/composables/useLaunchDarkly';
import { trackPendoEvent } from '@/utils/helpers/pendo';
import useTrackingDebugQuery from '@/platform/composables/useTrackingDebugQuery';

export { useBaseMetrics } from '@/platform/composables/useBaseMetrics';

export default function useExperiment() {
  const { client: ldClient, ldTrackCustomEvent } = useLaunchDarkly();
  const { isBypassExperimentsQueryEnabled } = useTrackingDebugQuery();

  // try to stringify
  const stringify = (value) => {
    try {
      return JSON.stringify(value);
    } catch {
      return value;
    }
  };

  /**
   * Push experiment participation and variation to Pendo, if user is part of an experiment. Only one call per LD flag will be passed through during lifetime of service.
   * @param {string} ldFlagKey Name of the LaunchDarkly key for the experiment, the experiment track event will only be sent if current user evaluation of this flag is inExperiment or if it is bypassed by the `bypassExperiments` query parameter as defined in `useTrackingDebugQuery.js`.
   * @param {*} defaultValue Default value for the variation to use if value could not be fetched.
   * @param {boolean} [nonLDExperiment] Parameter to bypass reason?.inExperiment check and send event to Pendo regardless of LD experiment status. `EXPERIMENT` event will only be sent if reason.kind is `RULE_MATCH` and nonLDExperiment is true.
   * More info on {@link https://docs.launchdarkly.com/sdk/concepts/evaluation-reasons#understanding-the-reason-data LaunchDarkly Evaluation Reasons}
   */
  const trackExperimentInPendo = (
    ldFlagKey,
    defaultValue,
    nonLDExperiment = false,
  ) => {
    ldClient.value.waitUntilReady().then(() => {
      const expVariationDetail = ldClient.value.variationDetail(
        ldFlagKey,
        defaultValue,
      );
      const ruleMatchAndNonLDExperiment =
        expVariationDetail.reason?.kind === 'RULE_MATCH' && nonLDExperiment;

      if (
        expVariationDetail.reason?.inExperiment ||
        ruleMatchAndNonLDExperiment ||
        isBypassExperimentsQueryEnabled.value
      ) {
        let experimentVariationName = '';

        switch (expVariationDetail.value) {
          case true:
            experimentVariationName = 'test';
            break;
          case false:
            experimentVariationName = 'control';
            break;
          default:
            experimentVariationName = stringify(expVariationDetail.value);
        }

        trackPendoEvent('EXPERIMENT', {
          experiment_id: ldFlagKey,
          experiment_variation_index: expVariationDetail.variationIndex,
          experiment_variation_value: stringify(expVariationDetail.value),
          experiment_variation_name: experimentVariationName,
          experiment_rule_id: expVariationDetail.reason?.ruleId,
        });
      }
    });
  };

  /**
   * Submit a Pendo tracking event if user is in an active Launch Darkly experiment
   * @param {string} ldFlagKey Name of the LaunchDarkly key for the experiment
   * @param {string} eventName pendo event key
   * @param {Object} metadata pendo metadata object
   */
  const trackPendoEventIfInExperiment = (ldFlagKey, eventName, metadata) => {
    ldClient.value.waitUntilReady().then(() => {
      const expVariationDetail = ldClient.value.variationDetail(ldFlagKey);

      if (
        expVariationDetail.reason?.inExperiment ||
        isBypassExperimentsQueryEnabled.value
      ) {
        trackPendoEvent(eventName, metadata);
      }
    });
  };

  /**
   * Submit a LaunchDarkly custom event if user is in an active Launch Darkly experiment
   * @param {string} ldFlagKey Name of the LaunchDarkly key for the flag
   * @param {string} eventName LaunchDarkly custom event key
   * @param {*} [data] LaunchDarkly data, and JSON serializable value
   * @param {*} [metricKeyValue] LaunchDarkly metric value
   */
  const trackLDEventIfInExperiment = (
    ldFlagKey,
    eventName,
    data,
    metricValue,
  ) => {
    ldClient.value.waitUntilReady().then(() => {
      const expVariationDetail = ldClient.value.variationDetail(ldFlagKey);

      if (expVariationDetail.reason?.inExperiment) {
        ldTrackCustomEvent(eventName, data, metricValue);
      }
    });
  };

  return {
    trackExperimentInPendo,
    trackPendoEvent,
    trackPendoEventIfInExperiment,
    trackLDEventIfInExperiment,
    ldTrackCustomEvent,
    ldVariationDetail: ldClient.value.variationDetail,
  };
}
